import i18next from 'i18next'
import { ChartDataset, ChartOptions, TooltipItem } from 'chart.js'
import { ChartLabels } from 'components/charts/types'
import { CHART_COLORS, getBarChartDefaults } from 'components/charts/constants'
import { createChartDataArray, formatChartDateLabel } from 'utils/chartjs'
import { PreviousBillingCycle } from 'pages/app/billing/billing-cycles/types'
import { formatAmount } from 'utils/format'
import { getChartPattern, getSettlementChartColor } from '../utils'
import { externalTooltipHandler } from 'components/charts/chart-tooltip/tooltip.ts'
import { ExternalTooltipContext } from 'components/charts/types.ts'
import { Granularity } from 'types/contracts.ts'
import { BillingCycleChartOptions } from 'components/charts/billing-cycle/types.ts'
import { TOOLTIP_CONFIG } from 'components/charts/chart-tooltip/config.ts'
import { DSChartColors } from 'types/colors.ts'

export const getBillingCycleChartOptions = ({
  labels,
  isMobile,
  handleHoverDataPoint
}: Omit<BillingCycleChartOptions, 'minMax'>): ChartOptions<'bar'> => {
  const defaults = getBarChartDefaults({ isMobile })
  const {
    scales: { y, x }
  } = defaults

  return {
    ...defaults,
    maintainAspectRatio: false,
    skipNull: true,
    scales: {
      y: {
        ...y,
        ticks: {
          ...y.ticks,
          callback: (value: string | number) => formatAmount(Number(value), { fractionDigits: 0 })
        }
      },
      x: {
        ...x,
        ticks: {
          callback: (value: string | number) => {
            const index = Number(value)
            if (index === labels.length - 1) return labels[index]
            return formatChartDateLabel(labels[index], Granularity.MONTH)
          }
        }
      }
    },
    plugins: {
      yScaleTitle: {
        labels: {
          y: '€'
        }
      },
      annotation: {
        common: {
          drawTime: 'beforeDraw'
        },
        annotations: {
          lastColumn: {
            type: 'box',
            backgroundColor: CHART_COLORS.backgroundHighlight,
            borderWidth: 0,
            xMax: 0.5 + labels.length - 1,
            xMin: -0.5 + labels.length - 1
          }
        }
      },
      tooltip: {
        ...TOOLTIP_CONFIG,
        external: (context: ExternalTooltipContext<'bar'>) => {
          const { tooltip } = context
          const currentDataPoint = tooltip?.dataPoints?.[0]?.dataIndex
          const hideTooltip = tooltip.opacity === 0
          handleHoverDataPoint(hideTooltip ? null : currentDataPoint)
          return externalTooltipHandler({ context, totalColumns: labels.length - 1 })
        },
        callbacks: {
          title: (context: TooltipItem<'bar'>[]) =>
            context?.[0]?.dataIndex === labels.length - 1
              ? i18next.t(($) => $.billingCycle.chart.settlement, {
                  ns: 'billing'
                })
              : formatChartDateLabel(context?.[0]?.label, Granularity.MONTH, true)
        }
      }
    }
  }
}

export const getBillingCycleChartDatasets = (
  labels: ChartLabels,
  data: PreviousBillingCycle,
  isMobile?: boolean
): ChartDataset<'bar', (number | null)[]>[] => {
  const maxBarThickness = 94
  const borderRadius = isMobile ? 2 : 4

  return [
    {
      label: i18next.t(($) => $.billingCycles.chart.legendPrevious.billedInstalment, {
        ns: 'billing'
      }),
      backgroundColor: CHART_COLORS.pink,
      data: createChartDataArray(
        labels.length,
        data.months.map(({ billedAmount }) => billedAmount)
      ),
      maxBarThickness,
      borderRadius
    },
    {
      label: i18next.t(($) => $.billingCycles.chart.legendPrevious.actualInstalment, {
        ns: 'billing'
      }),
      backgroundColor: getChartPattern(DSChartColors.GREEN),
      data: createChartDataArray(
        labels.length,
        data.months.map(({ actualPrice }) => actualPrice)
      ),
      maxBarThickness,
      borderRadius
    },
    {
      label: i18next.t(($) => $.billingCycles.chart.legendPrevious.settlement, {
        ns: 'billing'
      }),
      backgroundColor: getSettlementChartColor(data.settlement),
      data: createChartDataArray(labels.length, [data.settlement], labels.length - 1),
      maxBarThickness,
      borderRadius
    }
  ]
}
